// Typed helpers for the tasks (/api/tasks) and reply-tracking (/api/replies) routes. URL builders
// are pure so they can be unit-tested; thread keys travel as one encoded path segment.
import type {
  CardView,
  TaskCreateRequest,
  TaskListView,
  TaskPatchRequest,
  TaskView,
  ThreadStatusView,
} from "@shared/api";
import type { ThreadStatus } from "@shared/core";
import { api } from "./api";

const enc = encodeURIComponent;

export type TaskStatusOp = "done" | "reopen" | "drop";

export const taskUrls = {
  list: (view: TaskListView) => `/api/tasks?view=${enc(view)}`,
  task: (id: string) => `/api/tasks/${enc(id)}`,
  create: () => "/api/tasks",
  fromCard: (cardId: string) => `/api/tasks/from-card/${enc(cardId)}`,
  status: (id: string, op: TaskStatusOp) => `/api/tasks/${enc(id)}/${op}`,
  snooze: (id: string) => `/api/tasks/${enc(id)}/snooze`,
  forThread: (threadKey: string) => `/api/tasks/thread/${enc(threadKey)}`,
  replies: (status?: ThreadStatus, limit?: number) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (limit) params.set("limit", String(limit));
    const text = params.toString();
    return `/api/replies${text ? `?${text}` : ""}`;
  },
  replyWait: (threadKey: string) => `/api/replies/${enc(threadKey)}/wait`,
  replyDone: (threadKey: string) => `/api/replies/${enc(threadKey)}/done`,
};

export interface CaptureRequest {
  title?: string;
  due?: string;
  notes?: string;
  kind?: TaskView["kind"];
}

export const tasksApi = {
  list: (view: TaskListView, signal?: AbortSignal) =>
    api<TaskView[]>(taskUrls.list(view), { signal }),
  forThread: (threadKey: string) => api<TaskView[]>(taskUrls.forThread(threadKey)),
  get: (id: string, signal?: AbortSignal) => api<TaskView>(taskUrls.task(id), { signal }),
  create: (body: TaskCreateRequest) => api<TaskView>(taskUrls.create(), { method: "POST", body }),
  fromCard: (cardId: string, body: CaptureRequest = {}) =>
    api<{ task: TaskView; card: CardView }>(taskUrls.fromCard(cardId), { method: "POST", body }),
  patch: (id: string, body: TaskPatchRequest) =>
    api<TaskView>(taskUrls.task(id), { method: "PATCH", body }),
  setStatus: (id: string, op: TaskStatusOp) =>
    api<TaskView>(taskUrls.status(id, op), { method: "POST" }),
  snooze: (id: string, until: string) =>
    api<TaskView>(taskUrls.snooze(id), { method: "POST", body: { until } }),

  replies: (status: ThreadStatus | undefined, limit: number | undefined, signal?: AbortSignal) =>
    api<ThreadStatusView[]>(taskUrls.replies(status, limit), { signal }),
  waitMore: (threadKey: string, days: number) =>
    api<ThreadStatusView>(taskUrls.replyWait(threadKey), { method: "POST", body: { days } }),
  replyDone: (threadKey: string) =>
    api<ThreadStatusView>(taskUrls.replyDone(threadKey), { method: "POST" }),
};
